import {isFeatureEnabled} from '@github-ui/feature-flags'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'

/**
 * The canonical slug for the default Copilot agent as used by the monolith.
 * Used to determine if an agent is the built-in Copilot agent (whose display name
 * is feature-flagged as "Copilot cloud agent" or "Copilot coding agent").
 */
export const COPILOT_AGENT_SLUG = 'copilot-swe-agent'

/**
 * The slug for the Copilot cloud agent as used by CMC/CAPI.
 * CMC returns this slug in task data, while the monolith agents endpoint
 * returns COPILOT_AGENT_SLUG. They refer to the same agent.
 */
export const COPILOT_CMC_SLUG = 'copilot-developer'

/**
 * The slug for the Copilot Developer CLI agent.
 */
export const COPILOT_CLI_SLUG = 'copilot-developer-cli'

export const COPILOT_APP_SLUG = 'copilot-developer-app'

/** App sessions temporarily share CLI session UI, not CLI launch/resume commands. */
export function isCopilotCLICompatibleAgent(agentSlug: string | undefined | null): boolean {
  return agentSlug === COPILOT_CLI_SLUG || (agentSlug === COPILOT_APP_SLUG && copilotFeatureFlags.appRemoteSessions)
}

/**
 * The slug for the VS Code Chat agent.
 */
export const COPILOT_VSCODE_SLUG = 'vscode-chat'

/**
 * The slug for the JetBrains Chat agent.
 */
export const COPILOT_JETBRAINS_SLUG = 'jetbrains-chat'

const CCR_AGENT_IDS = [946600, 71, 106, 776, 386, 56]

export function isCCRAgent(session: {agent_id: number} | null) {
  return !!session && CCR_AGENT_IDS.includes(session.agent_id)
}

export function isReadonlyAgent(session: {agent_id: number} | null) {
  return isCCRAgent(session)
}

/**
 * Agent slugs that support subagent log grouping.
 */
const SUBAGENT_GROUPING_SLUGS: ReadonlySet<string> = new Set([
  COPILOT_AGENT_SLUG,
  COPILOT_CMC_SLUG,
  COPILOT_CLI_SLUG,
  COPILOT_VSCODE_SLUG,
  COPILOT_JETBRAINS_SLUG,
])

/**
 * Checks whether an agent slug matches the built-in Copilot agent slug.
 * Note: this is tied to the canonical slug, not the display name, which is
 * feature-flagged separately.
 * @param agentSlug - The slug of the agent to check
 * @returns true if the agent matches the default Copilot agent slug
 */
export function isCopilotCodingAgent(agentSlug: string | undefined | null): boolean {
  return agentSlug === COPILOT_AGENT_SLUG || agentSlug === COPILOT_CMC_SLUG
}

/**
 * Checks if an agent supports subagent log grouping (collapsible subagent sections).
 * @param agentSlug - The slug of the agent to check
 * @returns true if the agent supports subagent grouping
 */
export function agentSupportsSubagentGrouping(agentSlug: string | undefined | null): boolean {
  return !!agentSlug && (SUBAGENT_GROUPING_SLUGS.has(agentSlug) || isCopilotCLICompatibleAgent(agentSlug))
}

/**
 * Checks if the selected agent is Copilot or a custom agent (not a third-party agent).
 * Returns true for:
 * - Custom agents (no topLevelAgentId)
 * - Copilot cloud agent
 * - SubAgents
 * @param selectedAgent - The currently selected agent
 * @param agentSlug - The slug of the top-level agent (from agentInfo)
 * @returns true if the agent is Copilot or a custom agent
 */
export function isCopilotOrCustomAgent(
  selectedAgent: {topLevelAgentId?: string | null; subAgentSlug?: string | null},
  agentSlug: string | undefined,
): boolean {
  return !selectedAgent.topLevelAgentId || isCopilotCodingAgent(agentSlug) || !!selectedAgent.subAgentSlug
}

/**
 * Checks if an agent supports model selection.
 * When the `coding_agent_third_party_model_ui` feature flag is enabled, returns true for all agents
 * (Copilot, custom agents, and third-party agents like Claude and Codex).
 * When the flag is disabled, falls back to the original isCopilotOrCustomAgent behavior.
 */
export function agentSupportsModelSelection(
  selectedAgent: {topLevelAgentId?: string | null; subAgentSlug?: string | null},
  agentSlug?: string,
): boolean {
  if (isFeatureEnabled('coding_agent_third_party_model_ui')) {
    return true
  }
  return isCopilotOrCustomAgent(selectedAgent, agentSlug)
}

/**
 * Returns the model vendor filters for a given agent.
 * Uses the server-provided `model_vendors` from AgentInfo.
 * Third-party agents return their vendors (e.g., ["anthropic"], ["openai"]) so the model picker
 * can filter to only show models from those vendors.
 * Copilot and custom agents return undefined (show all models).
 */
export function getAgentModelVendors(serverVendors?: string[] | null): string[] | undefined {
  if (!serverVendors || serverVendors.length === 0) return undefined
  return serverVendors
}

/**
 * Checks if a vendor has at least one available model in the provided models list.
 * Returns false if all models for the vendor are disabled.
 * @param vendorName - The vendor name to check (e.g., "anthropic", "openai")
 * @param models - The list of available models
 * @returns true if the vendor has at least one available model, false otherwise
 */
export function hasModelsForVendor(
  vendorName: string | undefined | null,
  models: Array<{vendor?: string; model_picker_enabled?: boolean}>,
): boolean {
  if (!vendorName) return true // If no vendor specified, always show the agent

  const vendorLower = vendorName.toLowerCase()
  return models.some(model => model.vendor?.toLowerCase().includes(vendorLower) && model.model_picker_enabled !== false)
}

/**
 * Checks if an agent with multiple vendors has at least one available model.
 * Returns true if any vendor in the array has models, or if the vendors array is empty/undefined.
 * @param vendors - The vendor names to check (e.g., ["anthropic", "openai"])
 * @param models - The list of available models
 * @returns true if any vendor has at least one available model
 */
export function hasModelsForVendors(
  vendors: string[] | undefined | null,
  models: Array<{vendor?: string; model_picker_enabled?: boolean}>,
): boolean {
  if (!vendors || vendors.length === 0) return true
  return vendors.some(v => hasModelsForVendor(v, models))
}

/**
 * Detects newly added agents when assignee selection changes.
 * Returns all newly added agent assignees and the assignees that should be
 * saved immediately (without the modal).
 *
 * Note: The `nonAgentAssignees` return value includes:
 * - All non-agent assignees
 * - Any agent assignees that were already assigned before (i.e. not newly added)
 */
export function detectNewlyAddedAgents<TCurrent, TSelected = TCurrent>(
  currentAssignees: TCurrent[],
  selectedAssignees: TSelected[],
  isAgent: (assignee: TCurrent | TSelected) => boolean,
  getLogin: (assignee: TCurrent | TSelected) => string,
): {newlyAddedAgents: TSelected[]; nonAgentAssignees: TSelected[]} {
  const previousAgentLogins = new Set(currentAssignees.filter(isAgent).map(getLogin))
  const addedAgents = selectedAssignees.filter(a => isAgent(a) && !previousAgentLogins.has(getLogin(a)))
  const assigneesToSave = selectedAssignees.filter(a => !isAgent(a) || previousAgentLogins.has(getLogin(a)))

  return {
    newlyAddedAgents: addedAgents,
    nonAgentAssignees: assigneesToSave,
  }
}
